import React, { useState } from 'react';
import { Container, Row, Col, Spinner, Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import CustomDatePicker from '../common/CustomDatePicker';
import InputBox from '../common/InputBox';
import { listReversibleInvoices, rollbackIod, getCurrentFreeze } from '../../services/iod/iod-service';
import styles from './iod.module.css';

const EMPTY_FILTERS = { division: '', company: '', fromDate: '', toDate: '' };

const IodReverse = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Rollback confirm
  const [confirmRow, setConfirmRow] = useState(null);
  const [freezeInfo, setFreezeInfo] = useState(null);
  const [rolling, setRolling] = useState(false);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async () => {
    setLoading(true);
    setSearched(false);
    try {
      const res = await listReversibleInvoices(filters);
      setInvoices(res.data || []);
      setSearched(true);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setInvoices([]);
    setSearched(false);
  };

  const openConfirm = async (row) => {
    setConfirmRow(row);
    try {
      const res = await getCurrentFreeze();
      setFreezeInfo(res.data || null);
    } catch {
      setFreezeInfo(null);
    }
  };

  const handleRollback = async () => {
    if (!confirmRow) return;
    setRolling(true);
    try {
      await rollbackIod(confirmRow.company, confirmRow.transactionType, confirmRow.invoiceNo);
      toast.success(`IOD reversed for invoice ${confirmRow.invoiceNo}`);
      setConfirmRow(null);
      setFreezeInfo(null);
      await handleSearch();
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Rollback failed');
    } finally {
      setRolling(false);
    }
  };

  const isFrozen = freezeInfo && confirmRow
    ? confirmRow.iodDate >= freezeInfo.fromDate && confirmRow.iodDate <= freezeInfo.toDate
    : false;

  return (
    <>
      {loading && (
        <div className="spinner-box">
          <Spinner animation="border" className="spinner" />
        </div>
      )}

      <Container bsPrefix="main-body">
        <div className="page-title">IOD Reverse</div>
        <div className="page-sub-title">Rollback Invoice on Delivery date to Pending status</div>

        {/* Filter bar */}
        <div className={styles['filter-bar']}>
          <Row className="g-2 align-items-end">
            <Col md={2}>
              <InputBox type="text" label="Division" name="division" value={filters.division} onChange={handleFilterChange} />
            </Col>
            <Col md={2}>
              <InputBox type="text" label="Company" name="company" value={filters.company} onChange={handleFilterChange} />
            </Col>
            <Col md={2}>
              <CustomDatePicker label="From Date" name="fromDate" value={filters.fromDate} onChange={handleFilterChange} />
            </Col>
            <Col md={2}>
              <CustomDatePicker label="To Date" name="toDate" value={filters.toDate} onChange={handleFilterChange} />
            </Col>
            <Col md="auto">
              <div className={styles['filter-actions']}>
                <button className={`btn ${styles['btn-iod-primary']}`} onClick={handleSearch}>Search</button>
                <button className={`btn ${styles['btn-iod-outline']}`} onClick={handleReset}>Reset</button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Stats */}
        {searched && (
          <div className={styles['stats-strip']}>
            <div className={styles['stat-card']}>
              <div className={styles['stat-value']}>{invoices.length}</div>
              <div className={styles['stat-label']}>Reversible</div>
            </div>
          </div>
        )}

        {/* Table */}
        {searched && (
          <div className={styles['iod-table-wrap']}>
            <table className={`table ${styles['iod-table']}`}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Invoice No</th>
                  <th>Company</th>
                  <th>Txn Type</th>
                  <th>Division</th>
                  <th>Transporter</th>
                  <th>Load No</th>
                  <th>Invoice Date</th>
                  <th>IOD Date</th>
                  <th>IOD Time</th>
                  <th>Updated By</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan={12} className="text-center text-muted py-4">No reversible invoices found</td></tr>
                ) : (
                  invoices.map((row, idx) => (
                    <tr key={`${row.company}|${row.transactionType}|${row.invoiceNo}`}>
                      <td>{idx + 1}</td>
                      <td>{row.invoiceNo}</td>
                      <td>{row.company}</td>
                      <td>{row.transactionType}</td>
                      <td>{row.division}</td>
                      <td>{row.transporterCode}</td>
                      <td>{row.loadNumber}</td>
                      <td>{row.invoiceDate}</td>
                      <td>{row.iodDate}</td>
                      <td>{row.iodTime || '—'}</td>
                      <td>{row.updatedBy || '—'}</td>
                      <td>
                        <button
                          className={`btn btn-sm ${styles['btn-iod-outline']}`}
                          onClick={() => openConfirm(row)}
                        >
                          Reverse
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Container>

      {/* Rollback confirmation modal */}
      <Modal show={!!confirmRow} onHide={() => { setConfirmRow(null); setFreezeInfo(null); }} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Confirm IOD Reversal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isFrozen && (
            <div className={styles['rollback-warning']}>
              <strong>⚠ Freeze Period Active</strong>
              IOD date {confirmRow?.iodDate} falls within the current freeze period
              ({freezeInfo?.fromDate} – {freezeInfo?.toDate}). Reversal is blocked.
            </div>
          )}
          {!isFrozen && (
            <p style={{ fontSize: '0.9rem' }}>
              Are you sure you want to reverse IOD for invoice{' '}
              <strong>{confirmRow?.invoiceNo}</strong> (Company: {confirmRow?.company})?
              <br />
              <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                Current IOD date: {confirmRow?.iodDate} — status will revert to <strong>PENDING</strong>.
              </span>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setConfirmRow(null); setFreezeInfo(null); }}>
            Cancel
          </Button>
          {!isFrozen && (
            <Button
              style={{ background: '#810055', borderColor: '#810055' }}
              onClick={handleRollback}
              disabled={rolling}
            >
              {rolling ? 'Reversing…' : 'Confirm Reverse'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default IodReverse;
